import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AnimationProvider, useAnimations } from "./context/AnimationContext";
import { UnreadProvider } from "./context/UnreadContext";
import { Toaster } from "./components/ui/sonner";
import OfflineBanner from "./components/OfflineBanner";
import GlobalSearch from "./components/GlobalSearch";
import { OnboardingProvider, OnboardingPageTip } from "./components/OnboardingTour";
import InstallPrompt from "./components/InstallPrompt";
import SWUpdateBanner from "./components/SWUpdateBanner";
import CGUReAcceptModal from "./components/CGUReAcceptModal";
import AuthCallback from "./components/AuthCallback";
import DossierLoadingScreen from "./components/DossierLoadingScreen";
import MobileNav from "./components/MobileNav";
import CabineApplier from "./components/CabineApplier";
import TopBar from "./components/TopBar";

// Pages
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";
import EventDetailPage from "./pages/EventDetailPage";
import DirectMessagesPage from "./pages/DirectMessagesPage";
import AdminPage from "./pages/AdminPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import VictoriesPage from "./pages/VictoriesPage";
import IdeasPage from "./pages/IdeasPage";
import SeasonsPage from "./pages/SeasonsPage";
import ResourcesPage from "./pages/ResourcesPage";
import QuestionsPage from "./pages/QuestionsPage";
import MyStatsPage from "./pages/MyStatsPage";
import ProfilePage from "./pages/ProfilePage";
import ChallengesPage from "./pages/ChallengesPage";
import AmbassadorPage from "./pages/AmbassadorPage";
import MonCarnetPage from "./pages/MonCarnetPage";
import MemberProfilePage from "./pages/MemberProfilePage";
import ObjectivesPage from "./pages/ObjectivesPage";
import EventAttendancePage from "./pages/EventAttendancePage";
import MembersMapPage from "./pages/MembersMapPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProgressionPage from "./pages/ProgressionPage";
import CommunityFeedPage from "./pages/CommunityFeedPage";
import ReplaysPage from "./pages/ReplaysPage";
import ForcePasswordChangePage from "./pages/ForcePasswordChangePage";
import VigieImmobilierePage from "./pages/VigieImmobilierePage";
import RewardsPage from "./pages/RewardsPage";
import ThemePreviewPage from "./pages/ThemePreviewPage";
import IconsPreviewPage from "./pages/IconsPreviewPage";
import IconsColorPreviewPage from "./pages/IconsColorPreviewPage";
import SplashDemoPage from "./pages/SplashDemoPage";
import PasserellePage from "./pages/PasserellePage";
import CabinesPage from "./pages/CabinesPage";
import JourneyPage from "./pages/JourneyPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowPasswordChange = false }) => {
  const { user, loading, api } = useAuth();
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  
  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  if (loading) {
    return <DossierLoadingScreen title="L'Équipage Odys" />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Force password change redirect - no overlays
  if (user.must_change_password && allowPasswordChange) {
    return children;
  }
  if (user.must_change_password && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }
  
  return (
    <>
      <TopBar onSearchClick={() => setShowSearch(true)} />
      {children}
      <CabineApplier />
      <MobileNav />
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <OnboardingPageTip />
      <InstallPrompt />
      <SWUpdateBanner />
      <CGUReAcceptModal />
    </>
  );
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <DossierLoadingScreen title="L'Équipage Odys" />;
  }
  
  if (user) {
    if (user.must_change_password) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// App Routes
const AppRoutes = () => {
  // CRITICAL: Emergent OAuth returns to /dashboard#session_id=... — we must
  // intercept the callback SYNCHRONOUSLY during render (not in useEffect)
  // before AuthProvider runs /auth/me and before any ProtectedRoute check.
  // Use useLocation().hash — window.location.hash is not reactive.
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowPasswordChange>
            <ForcePasswordChangePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/channels"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar/event/:eventId"
        element={
          <ProtectedRoute>
            <EventDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dm"
        element={
          <ProtectedRoute>
            <DirectMessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dm/:recipientId"
        element={
          <ProtectedRoute>
            <DirectMessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events/:eventId/attendance"
        element={
          <ProtectedRoute>
            <EventAttendancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/victories"
        element={
          <ProtectedRoute>
            <VictoriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ideas"
        element={
          <ProtectedRoute>
            <IdeasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seasons"
        element={
          <ProtectedRoute>
            <SeasonsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <ResourcesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/questions"
        element={
          <ProtectedRoute>
            <QuestionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-stats"
        element={
          <ProtectedRoute>
            <MyStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/challenges"
        element={
          <ProtectedRoute>
            <ChallengesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ambassador"
        element={
          <ProtectedRoute>
            <AmbassadorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/carnet"
        element={
          <ProtectedRoute>
            <MonCarnetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members/:userId"
        element={
          <ProtectedRoute>
            <MemberProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objectives"
        element={
          <ProtectedRoute>
            <ObjectivesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members-map"
        element={
          <ProtectedRoute>
            <MembersMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/progression"
        element={
          <ProtectedRoute>
            <ProgressionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <CommunityFeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/replays"
        element={
          <ProtectedRoute>
            <ReplaysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vigie"
        element={
          <ProtectedRoute>
            <VigieImmobilierePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/theme-preview"
        element={
          <ProtectedRoute>
            <ThemePreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/icons-preview"
        element={
          <ProtectedRoute>
            <IconsPreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/icons-color-preview"
        element={
          <ProtectedRoute>
            <IconsColorPreviewPage />
          </ProtectedRoute>
        }
      />
      <Route path="/splash-demo" element={<SplashDemoPage />} />
      <Route
        path="/frise"
        element={
          <ProtectedRoute>
            <JourneyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cabines"
        element={
          <ProtectedRoute>
            <CabinesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/passerelle"
        element={
          <ProtectedRoute>
            <PasserellePage />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <OfflineBanner />
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <UnreadProvider>
              <AnimationProvider>
                <OnboardingProvider>
                  <AppRoutes />
                </OnboardingProvider>
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: 'hsl(var(--card))',
                      color: 'hsl(var(--foreground))',
                      border: '1px solid hsl(var(--border))',
                    },
                  }}
                />
              </AnimationProvider>
            </UnreadProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
